import type { components } from '@scalty/contracts';
import type { StudioContext } from './studio';
import { collect, memoryQuotable, memoryWord, modelRow, projectMeta } from './studio';
import { citationDocumentNames } from './citationDocumentNames';
import { formatRelativeTime } from '~/utils/relativeTime';
import { plural } from '~/utils/words';

type S = components['schemas'];
const time = (input?: string | null) => input ? formatRelativeTime(input) : '—';

/**
 * One row of the model chooser. Every text version of the project is a row, ready or not:
 * a run still training is a fact the customer caused and can see, whereas leaving it out
 * would read as "this project has no model". Only a `ready` row can be picked, and each
 * row carries the facts the Model card shows, so choosing one costs no second read.
 */
export interface PlaygroundModelChoice {
  id: string;
  title: string;
  facts: string;
  status: string;
  ready: boolean;
  kind: string;
  type: string;
  base: string;
  /**
   * The collection this version was trained to answer from, or '' when it was not trained
   * that way. A version built from a `documents` dataset learns to answer from evidence
   * supplied with the question — the dataset's own collection — which is why a deployment
   * of it binds that collection by default (services/api/internal/domain/deploymentmemory).
   * Carried here so the playground can start from the same evidence rather than from none.
   */
  groundedBy: string;
}

/**
 * A ready trained version can answer with this collection. Without one, the page opens
 * the existing training action. The collection may belong to a project other than the
 * browser's current selection, so read that project's versions when necessary.
 */
export async function askMemoryPath(ctx: StudioContext, collection: S['KnowledgeBase'], models: S['Model'][]): Promise<string | null> {
  const projectModels = ctx.projectId === collection.projectId ? models
    : await collect<S['Model']>(ctx, `/models?project=${encodeURIComponent(collection.projectId)}`, 'models');
  const ready = projectModels.find((model) => model.projectId === collection.projectId
    && model.modelType === 'text_generation' && model.status === 'succeeded' && !model.archivedAt);
  return ready ? `/playground?model=${encodeURIComponent(ready.id)}&memory=${encodeURIComponent(collection.id)}` : null;
}

/**
 * What the playground reads: which trained versions can hold a conversation, the project's
 * memory collections, and the conversations already saved. Nothing here reports a state the
 * customer has not caused — with no version chosen there is no model object at all, so the
 * page says what to do in words instead of drawing an entity with a status word.
 *
 * A conversation is addressed by model version (POST /models/{id}/playground), never by
 * deployment, so a version can be tried the moment its run succeeds.
 */
export async function playgroundView(ctx: StudioContext, models: S['Model'][]) {
  /* Only a text version holds a conversation; a tabular one is called for predictions. */
  const talkable = models.filter((model) => model.modelType === 'text_generation' && !model.archivedAt);
  const ready = talkable.filter((model) => model.status === 'succeeded');
  const collections = ctx.projectId ? await collect<S['KnowledgeBase']>(ctx, `/knowledge-bases?project=${encodeURIComponent(ctx.projectId)}`, 'knowledgeBases') : [];
  /* The name to put on a citation chip, by the id the citation carries: the customer's own file
     name, never the `doc_…` handle they have never seen (W8-UI-02). */
  const documentNames = await citationDocumentNames(ctx, ctx.projectId);
  /* One page of the newest conversations. The endpoint answers with a cursor, not a count, so
     the page total is known only when there is nothing after this page. */
  const conversationPage = ctx.projectId ? await ctx.get<S['ConversationList']>(`/projects/${encodeURIComponent(ctx.projectId)}/conversations?limit=20`) : { conversations: [], nextCursor: null };
  const conversations = conversationPage.conversations;
  /* Which dataset each version came from, and the collection that dataset carries. */
  const datasets = ctx.projectId ? await collect<S['Dataset']>(ctx, `/datasets?project=${encodeURIComponent(ctx.projectId)}`, 'datasets') : [];
  const groundingByDataset = new Map(datasets
    .filter((dataset) => dataset.kind === 'documents' && dataset.memoryKnowledgeBaseId)
    .map((dataset) => [dataset.id, dataset.memoryKnowledgeBaseId as string]));
  const choices: PlaygroundModelChoice[] = talkable.map((model) => ({
    id: model.id,
    groundedBy: groundingByDataset.get(model.datasetId) ?? '',
    title: `${model.name} v${model.version}`,
    /* Searchable by the words a customer remembers: the base model and when the run happened. */
    facts: [model.baseModel ?? 'base model not recorded', time(model.createdAt)].join(' · '),
    status: modelRow(model, talkable).status,
    ready: model.status === 'succeeded',
    kind: 'Custom model',
    type: 'Text generation',
    base: model.baseModel ?? '—',
  }));
  return {
    meta: await projectMeta(ctx),
    choices,
    choicesMeta: ready.length === talkable.length ? plural(talkable.length, 'version') : `${ready.length} of ${plural(talkable.length, 'version')} ready`,
    /* Said only when the list is empty, and only from what the project really holds. */
    choicesEmpty: ctx.projectId ? 'No text model in this project yet. Train one and it appears here.' : 'Choose a project first.',
    /* `quotable` is the server's verdict carried through, so the panel and the line under it say
       what the collection can actually do instead of deciding from its word (W8-UI-10). */
    memory: collections.map((collection) => ({ id: collection.id, name: collection.name, status: memoryWord(collection), quotable: memoryQuotable(collection), picked: false })),
    documentNames,
    params: { temperature: 0.2, topP: 0.9, maxTokens: 512, system: '' },
    /* A conversation is titled from its first question; one saved before that has no title,
       and the list says so rather than showing an internal id as a name. */
    conversations: { total: conversationPage.nextCursor ? null : conversations.length, recent: conversations.map((conversation) => ({ id: conversation.id, title: conversation.title ?? 'Untitled conversation', at: time(conversation.updatedAt) })) },
  };
}
